/**
 * Shared building blocks for the Home page sections.
 *
 * Every colour on Home comes through `useHomeTokens()` → `getColor(token, colorMode)`.
 * The design is dark-first (translucent white hairlines on #08090a); `alpha()` turns
 * the *token* into the translucent variant so light mode gets translucent-dark
 * hairlines instead of invisible white ones. No raw hex anywhere below.
 */
import { ReactNode } from "react";
import {
  Box,
  BoxProps,
  Button,
  Flex,
  Heading,
  HeadingProps,
  SimpleGrid,
  SimpleGridProps,
  Text,
  TextProps,
  useColorMode,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { getColor } from "../../brandConfig";

/** Geist Mono kickers — the theme exposes no mono var yet, so the stack is the fallback. */
export const MONO = "var(--brand-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)";

/** rgba() of a brand token at a given alpha. Accepts #rgb, #rrggbb(aa), rgb(), rgba(). */
export function alpha(color: string, a: number): string {
  const c = color.trim();
  if (c.startsWith("#")) {
    const h = c.slice(1);
    const full = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h.slice(0, 6);
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return color;
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const [r, g, b] = m[1].split(",").map((s) => s.trim());
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return color;
}

export interface HomeTokens {
  colorMode: "light" | "dark";
  isDark: boolean;
  /** Page background (`background.main`). */
  bg: string;
  /** Alternating section band (`background.sectionAlt`). */
  bgAlt: string;
  text: string;
  textSoft: string;
  textMuted: string;
  textFaint: string;
  /** 1px section dividers. */
  hairline: string;
  /** Card outlines (`border.darkCard`). */
  cardBorder: string;
  cardGradient: string;
  cardTint: string;
  cardTintHover: string;
  primary: string;
  primaryHover: string;
  secondary: string;
  /** Kicker / small-caps label colour. */
  kicker: string;
  primaryTint: string;
  primaryBorder: string;
  primaryBorderHover: string;
  gold: string;
  goldBright: string;
  goldTint: string;
  goldBorder: string;
  purple: string;
  green: string;
  greenTint: string;
  greenBorder: string;
  grayTint: string;
  grayBorder: string;
  /** The design's "white button": text colour as a fill, page colour as the label. */
  inverseBg: string;
  inverseText: string;
}

export function useHomeTokens(): HomeTokens {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const text = getColor("text.primary", colorMode);
  const primary = getColor("primary", colorMode);
  const gold = getColor("programGold", colorMode);
  return {
    colorMode,
    isDark,
    bg: getColor("background.main", colorMode),
    bgAlt: getColor("background.sectionAlt", colorMode),
    text,
    textSoft: getColor("text.secondary", colorMode),
    textMuted: getColor("text.muted", colorMode),
    textFaint: alpha(text, isDark ? 0.36 : 0.55),
    hairline: alpha(text, isDark ? 0.07 : 0.1),
    cardBorder: getColor("border.darkCard", colorMode),
    cardGradient: getColor("background.cardGradient", colorMode),
    cardTint: alpha(text, isDark ? 0.022 : 0.03),
    cardTintHover: alpha(text, isDark ? 0.045 : 0.05),
    primary,
    primaryHover: getColor("primaryHover", colorMode),
    secondary: getColor("secondary", colorMode),
    kicker: isDark ? getColor("accentLight", colorMode) : getColor("primaryHover", colorMode),
    primaryTint: alpha(primary, isDark ? 0.08 : 0.06),
    primaryBorder: alpha(primary, isDark ? 0.26 : 0.35),
    primaryBorderHover: alpha(primary, 0.45),
    gold,
    goldBright: getColor("programGoldBright", colorMode),
    goldTint: alpha(gold, isDark ? 0.12 : 0.08),
    goldBorder: alpha(gold, isDark ? 0.35 : 0.4),
    purple: getColor("purpleAccent", colorMode),
    green: getColor("successGreen", colorMode),
    greenTint: getColor("badges.green.bg", colorMode),
    greenBorder: getColor("badges.green.border", colorMode),
    grayTint: getColor("badges.gray.bg", colorMode),
    grayBorder: getColor("badges.gray.border", colorMode),
    inverseBg: text,
    inverseText: getColor("background.main", colorMode),
  };
}

/* ------------------------------------------------------------------ */
/* Typography                                                          */
/* ------------------------------------------------------------------ */

/** Small-caps mono label above a heading ("THE DISCONNECT"). */
export const Kicker = ({ children, ...rest }: TextProps) => {
  const t = useHomeTokens();
  return (
    <Text
      as="span"
      fontFamily={MONO}
      fontSize="11.5px"
      letterSpacing=".14em"
      textTransform="uppercase"
      color={t.kicker}
      {...rest}
    >
      {children}
    </Text>
  );
};

/** Plain mono text (proof strip, tag lines, rung labels). */
export const Mono = (props: TextProps) => (
  <Text as="span" fontFamily={MONO} fontSize="11px" letterSpacing=".1em" {...props} />
);

export const H2 = (props: HeadingProps) => {
  const t = useHomeTokens();
  return (
    <Heading
      as="h2"
      fontSize="clamp(2rem, 3.4vw, 2.9rem)"
      lineHeight="1.1"
      fontWeight="600"
      letterSpacing="-0.025em"
      color={t.text}
      {...props}
    />
  );
};

export const H3 = (props: HeadingProps) => {
  const t = useHomeTokens();
  return (
    <Heading
      as="h3"
      fontSize="1.2rem"
      lineHeight="1.25"
      fontWeight="600"
      letterSpacing="-0.025em"
      color={t.text}
      {...props}
    />
  );
};

export const Lead = (props: TextProps) => {
  const t = useHomeTokens();
  return <Text fontSize="1.05rem" lineHeight="1.65" color={t.textSoft} {...props} />;
};

export const Body = (props: TextProps) => {
  const t = useHomeTokens();
  return <Text fontSize=".94rem" lineHeight="1.6" color={t.textSoft} {...props} />;
};

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

interface SectionProps extends BoxProps {
  id: string;
  /** Use the alternating band colour. */
  alt?: boolean;
}

/** A full-width page band. `id` is the nav anchor; `scrollMarginTop` clears the site navbar. */
export const Section = ({ id, alt, children, ...rest }: SectionProps) => {
  const t = useHomeTokens();
  return (
    <Box
      as="section"
      id={id}
      position="relative"
      overflow="hidden"
      borderTop="1px solid"
      borderColor={t.hairline}
      bg={alt ? t.bgAlt : "transparent"}
      py={{ base: 16, md: 28 }}
      px={6}
      scrollMarginTop="80px"
      {...rest}
    >
      {children}
    </Box>
  );
};

export const Inner = (props: BoxProps) => (
  <Box maxW="1200px" mx="auto" position="relative" zIndex={2} {...props} />
);

interface IntroProps {
  kicker: string;
  title: ReactNode;
  lead?: ReactNode;
  maxW?: string;
  mb?: BoxProps["mb"];
}

/** Kicker + h2 + lead paragraph, left-aligned, the standard section opener. */
export const Intro = ({ kicker, title, lead, maxW = "720px", mb = 14 }: IntroProps) => (
  <Flex direction="column" gap="18px" maxW={maxW} mb={mb}>
    <Kicker>{kicker}</Kicker>
    <H2>{title}</H2>
    {lead ? <Lead>{lead}</Lead> : null}
  </Flex>
);

/** Gradient-filled bordered card (the design's default panel). */
export const Card = ({ children, ...rest }: BoxProps) => {
  const t = useHomeTokens();
  return (
    <Box
      p={{ base: 6, md: 8 }}
      borderRadius="20px"
      borderWidth="1px"
      borderColor={t.cardBorder}
      bg={t.cardGradient}
      display="flex"
      flexDirection="column"
      gap="15px"
      minW={0}
      transition="border-color .2s ease, background .2s ease"
      _hover={{ borderColor: t.primaryBorder }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/** Hairline-separated cell grid (1px gaps on the border colour). Cells set their own `bg`. */
export const CellGrid = (props: SimpleGridProps) => {
  const t = useHomeTokens();
  return (
    <SimpleGrid
      gap="1px"
      bg={t.cardBorder}
      borderWidth="1px"
      borderColor={t.cardBorder}
      borderRadius="20px"
      overflow="hidden"
      {...props}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Lists                                                               */
/* ------------------------------------------------------------------ */

export const CheckList = ({ items, ...rest }: { items: string[] } & BoxProps) => {
  const t = useHomeTokens();
  return (
    <Box as="ul" listStyleType="none" display="grid" gap="10px" m={0} p={0} {...rest}>
      {items.map((item) => (
        <Flex as="li" key={item} gap="11px" fontSize=".92rem" color={t.textSoft} lineHeight="1.5">
          <Box as="span" color={t.primary} flexShrink={0} aria-hidden="true">
            ✓
          </Box>
          <Box as="span">{item}</Box>
        </Flex>
      ))}
    </Box>
  );
};

export const DotList = ({ items, ...rest }: { items: string[] } & BoxProps) => {
  const t = useHomeTokens();
  return (
    <Box as="ul" listStyleType="none" display="grid" gap="8px" m={0} p={0} {...rest}>
      {items.map((item) => (
        <Flex as="li" key={item} gap="10px" fontSize=".88rem" color={t.textSoft} lineHeight="1.5">
          <Box as="span" color={alpha(t.primary, 0.7)} flexShrink={0} aria-hidden="true">
            ·
          </Box>
          <Box as="span">{item}</Box>
        </Flex>
      ))}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Calls to action                                                     */
/* ------------------------------------------------------------------ */

interface CtaProps {
  /** Open an external/public page in a new tab (booking page). */
  newTab?: boolean;
  children: ReactNode;
  /** Mandatory tooltip — what the control does and whether it writes (it never does on Home). */
  title: string;
  /** React Router path (`/modules`). */
  to?: string;
  /** Plain href — in-page anchor (`#contact`), `mailto:` or `tel:`. */
  href?: string;
  variant?: "solid" | "ghost";
  size?: "md" | "sm";
  arrow?: boolean;
  alignSelf?: BoxProps["alignSelf"];
}

/** The design's two button styles: solid (page-inverse) and ghost (translucent). Links only — never a write. */
export const Cta = ({ children, title, to, href, variant = "solid", size = "md", arrow, alignSelf, newTab }: CtaProps) => {
  const t = useHomeTokens();
  const solid = variant === "solid";
  const styles = {
    bg: solid ? t.inverseBg : alpha(t.text, 0.06),
    color: solid ? t.inverseText : t.text,
    borderWidth: "1px",
    borderColor: solid ? t.inverseBg : alpha(t.text, 0.13),
    borderRadius: "11px",
    fontSize: size === "md" ? "15px" : ".92rem",
    fontWeight: "500",
    h: "auto",
    px: size === "md" ? "26px" : "22px",
    py: size === "md" ? "15px" : "13px",
    whiteSpace: "normal" as const,
    textAlign: "center" as const,
    _hover: { bg: solid ? t.inverseBg : alpha(t.text, 0.1), opacity: solid ? 0.9 : 1, textDecoration: "none" },
    alignSelf,
  };
  const label = (
    <>
      {children}
      {arrow ? (
        <Box as="span" opacity={0.4} ml="9px" aria-hidden="true">
          →
        </Box>
      ) : null}
    </>
  );
  if (to) {
    return (
      <Button as={RouterLink} to={to} title={title} {...styles}>
        {label}
      </Button>
    );
  }
  return (
    <Button
      as="a"
      href={href}
      title={title}
      {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      {...styles}
    >
      {label}
    </Button>
  );
};
